/**
 * The web app's single token authority.
 *
 * Every authenticated HTTP request, refresh, route gate, and voice WebSocket
 * reads this same store through ApiClient. Access tokens stay memory-only.
 * The rotating refresh token is scoped to sessionStorage so a reload can
 * restore the session, but closing the tab removes the durable browser copy.
 * This is the narrowest client persistence supported by the current
 * header-based server contract; moving refresh state into an HttpOnly cookie
 * would require a separate server/session migration.
 */

import type { AuthTokens, TokenStore } from '@zolotalk/api-client';

const REFRESH_TOKEN_KEY = 'zolotalk_refresh_token';

interface SessionStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function storage(): SessionStorage | null {
  try {
    return (globalThis as { sessionStorage?: SessionStorage }).sessionStorage ?? null;
  } catch {
    return null;
  }
}

function createSessionScopedTokenStore(): TokenStore {
  let accessToken: string | null = null;
  let accessTokenExpiry = 0;
  let refreshTokenFallback: string | null = null;

  const readRefreshToken = () => {
    try {
      return storage()?.getItem(REFRESH_TOKEN_KEY) ?? refreshTokenFallback;
    } catch {
      return refreshTokenFallback;
    }
  };

  return {
    getAccessToken: () => (Date.now() >= accessTokenExpiry ? null : accessToken),
    getRefreshToken: readRefreshToken,
    setTokens: (tokens: AuthTokens) => {
      accessToken = tokens.accessToken;
      accessTokenExpiry = Date.now() + tokens.expiresIn * 1000;
      refreshTokenFallback = tokens.refreshToken;
      try {
        storage()?.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
      } catch {
        // Current-tab auth remains usable; reload will fail closed to sign-in.
      }
    },
    clearTokens: () => {
      accessToken = null;
      accessTokenExpiry = 0;
      refreshTokenFallback = null;
      try {
        storage()?.removeItem(REFRESH_TOKEN_KEY);
      } catch {
        // The memory authority is cleared even when browser storage is blocked.
      }
    },
    isAccessTokenExpired: () => Date.now() >= accessTokenExpiry,
  };
}

export const webTokenStore = createSessionScopedTokenStore();
